import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../../from-server/asin-central/model";
import { serviceToDisplay } from "../../../util/dateUtils";
import {
  COLUMN_DEFINITIONS,
  formattedRestrictedPorAsinProgramName,
} from "../../porAsinList/PorAsinListTable-config";
import { ValidationServiceMetadata } from "../../porExport/porExport-Config";
import {
  ValidationAttribute,
  ValidationAttributeDetailStep,
} from "../validationAttribute";

// The step-column parsing below (unescape + JSON.parse of
// `validationDetailResult*`, `<Service>.<StepName>.<Label>` column
// naming, and the `Result` / `Reason` / `More_Information` label
// set) mirrors the legacy V1 implementation in
// `src/features/porExport/porExport.tsx` (`buildAsinStepNameMap` /
// `getStepNames` / `getAsinDetailKey` /
// `getValidationStepValuesForExport`). Both call sites must stay in
// sync until the legacy path retires with the V2 rollout — see the
// "Retire alongside `featureFlag.ts`" note on `V2Toggle.tsx`. If
// the DLCS escaping/serialization contract or the label set
// changes, update BOTH files.

type ExportRow = FullAsinWithAudits | RestrictedAsin;

const BUNDLE_ASIN_TYPES: ReadonlySet<string> = new Set([
  "soft_bundle",
  "vmp_bundle",
]);

const STEP_ATTRIBUTES: readonly {
  readonly label: "Result" | "Reason" | "More_Information";
  readonly pick: (step: ValidationAttributeDetailStep) => string | undefined;
}[] = [
  { label: "Result", pick: (s) => s.result },
  { label: "Reason", pick: (s) => s.reason },
  { label: "More_Information", pick: (s) => s.hint },
];

function isRestrictedAsin(row: ExportRow): row is RestrictedAsin {
  return "isRestricted" in row && row.isRestricted === true;
}

function stripBundleDescriptionMarkers(value: string): string {
  return value.replace(/\*(.*?)\*/g, "$1");
}

function bundleComponentQuantityToString(
  value: Record<string, number>
): string {
  return Object.entries(value)
    .map(([key, count]) => `${key}: ${count}`)
    .join(", ");
}

/**
 * Percentage rendering for the `validationStatus` column, mirroring
 * the old `porExportTransform`. Input is a comma-separated list of
 * step results (e.g. `PASS, FAIL, PASS`); output is the percentage
 * of PASS entries, rounded to a whole number.
 */
function validationStatusToPercentage(raw: unknown): string {
  if (typeof raw !== "string" || raw === "") {
    return "";
  }
  const entries = raw.split(",").map((s) => s.trim());
  if (entries.length === 0) {
    return "";
  }
  const passed = entries.filter((s) => s.includes("PASS")).length;
  return `${((passed / entries.length) * 100).toFixed(0)}%`;
}

function detailKey(asin: FullAsinWithAudits, stepColumnName: string): string {
  return `${asin.asin}.${asin.marketplace}.${stepColumnName}`;
}

function parseDetailAttribute(
  asin: FullAsinWithAudits,
  attributeName: string
): ValidationAttributeDetailStep[] {
  const raw = (asin as unknown as Record<string, unknown>)[attributeName];
  if (typeof raw !== "string" || raw === "") {
    return [];
  }
  try {
    // DLCS returns the value as a percent-escaped JSON string, same
    // as the display cells in `PorAsinListTable-config`.
    const parsed = JSON.parse(unescape(raw)) as ValidationAttribute;
    return parsed?.detail ?? [];
  } catch {
    return [];
  }
}

/**
 * Scan the ASIN set once to discover every service/step/attribute
 * triplet that appears in Hattrick or LVS detail results, and index
 * their values by `(asin, marketplace, stepColumnName)` so row
 * emission is O(1) per cell.
 *
 * Step column names have the form `<Service>.<StepName>.<Label>`
 * (e.g. `Hattrick.Registration.Result`). Insertion order is stable:
 * services in `ValidationServiceMetadata` order, then per-service
 * step discovery order.
 *
 * Restricted rows are skipped — they carry no detail attributes to
 * parse, and their step cells are emitted as empty in `buildRow`.
 */
function buildStepIndex(asins: readonly ExportRow[]): {
  readonly stepColumnNames: readonly string[];
  readonly valuesByKey: ReadonlyMap<string, string>;
} {
  const stepColumnNames: string[] = [];
  const seenColumnNames = new Set<string>();
  const valuesByKey = new Map<string, string>();

  for (const service of ValidationServiceMetadata) {
    for (const row of asins) {
      if (isRestrictedAsin(row)) {
        continue;
      }
      const steps = parseDetailAttribute(row, service.attributeName);
      for (const step of steps) {
        for (const stepAttr of STEP_ATTRIBUTES) {
          const columnName = `${service.serviceName}.${step.step}.${stepAttr.label}`;
          if (!seenColumnNames.has(columnName)) {
            seenColumnNames.add(columnName);
            stepColumnNames.push(columnName);
          }
          valuesByKey.set(
            detailKey(row, columnName),
            stepAttr.pick(step) ?? ""
          );
        }
      }
    }
  }

  return { stepColumnNames, valuesByKey };
}

/**
 * Deep-clone the asin, apply the mutations the old export applied
 * to the parsed copy (bundle description strip, bundleComponentQuantity
 * map flatten), then emit the columns in `COLUMN_DEFINITIONS` order
 * followed by the discovered step columns. Raw scalar values pass
 * through to papaparse, which stringifies scalars and treats `null`
 * / `undefined` as empty cells. Restricted rows emit empty step
 * cells because they have no detail attributes.
 */
function buildRow(
  row: ExportRow,
  columnIds: readonly string[],
  columnIndex: {
    readonly announceDate: number;
    readonly onlineStreetDate: number;
    readonly programName: number;
    readonly validationStatus: number;
  },
  stepColumnNames: readonly string[],
  valuesByKey: ReadonlyMap<string, string>
): unknown[] {
  const cloned = JSON.parse(JSON.stringify(row)) as Record<string, unknown>;

  const asinType = cloned["asinType"];
  const description = cloned["description"];
  if (
    typeof description === "string" &&
    description !== "" &&
    typeof asinType === "string" &&
    BUNDLE_ASIN_TYPES.has(asinType)
  ) {
    cloned["description"] = stripBundleDescriptionMarkers(description);
  }

  const bcq = cloned["bundleComponentQuantity"];
  if (bcq !== null && typeof bcq === "object") {
    cloned["bundleComponentQuantity"] = bundleComponentQuantityToString(
      bcq as Record<string, number>
    );
  }

  const rowCells: unknown[] = columnIds.map((id) => cloned[id]);

  if (columnIndex.announceDate !== -1) {
    const v = cloned["announceDate"];
    if (v) {
      rowCells[columnIndex.announceDate] = serviceToDisplay(v as Date);
    }
  }
  if (columnIndex.onlineStreetDate !== -1) {
    const v = cloned["onlineStreetDate"];
    if (v) {
      rowCells[columnIndex.onlineStreetDate] = serviceToDisplay(v as Date);
    }
  }
  if (columnIndex.programName !== -1 && isRestrictedAsin(row)) {
    rowCells[columnIndex.programName] =
      formattedRestrictedPorAsinProgramName(row);
  }
  if (columnIndex.validationStatus !== -1) {
    rowCells[columnIndex.validationStatus] = validationStatusToPercentage(
      cloned["validationStatus"]
    );
  }

  const stepCells: unknown[] = isRestrictedAsin(row)
    ? stepColumnNames.map(() => "")
    : stepColumnNames.map(
        (name) => valuesByKey.get(detailKey(row, name)) ?? ""
      );

  return [...rowCells, ...stepCells];
}

/**
 * Build the CSV grid — header row plus one row per ASIN — for the
 * validation dashboard's Export button. Emits every column in
 * `COLUMN_DEFINITIONS` from `PorAsinListTable-config` with the same
 * row transforms the legacy `porExport` applied (dates via
 * `serviceToDisplay`, bundle-description italic-marker strip,
 * `bundleComponentQuantity` map flatten, `validationStatus`
 * percentage, restricted program label), followed by per-service
 * per-step columns for Hattrick and LVS.
 *
 * Step columns are named `<Service>.<StepName>.<Label>` (where
 * Label is `Result`, `Reason`, or `More_Information`) and are
 * discovered dynamically from the parsed `validationDetailResult*`
 * JSON on each ASIN, so the emitted column set is exactly the union
 * of steps that appear in the matched ASINs. Services covered are
 * the two in `ValidationServiceMetadata` (Hattrick, LVS) — the
 * manual validation services do not populate step detail today.
 */
export function buildValidationExportCsv(
  asins: readonly ExportRow[]
): unknown[][] {
  const columnIds: string[] = COLUMN_DEFINITIONS.map((col: { id: string }) =>
    String(col.id)
  );
  const columnHeaders: string[] = COLUMN_DEFINITIONS.map(
    (col: { header: string }) => String(col.header ?? "")
  );

  const columnIndex = {
    announceDate: columnIds.indexOf("announceDate"),
    onlineStreetDate: columnIds.indexOf("onlineStreetDate"),
    programName: columnIds.indexOf("programName"),
    validationStatus: columnIds.indexOf("validationStatus"),
  };

  const { stepColumnNames, valuesByKey } = buildStepIndex(asins);

  const headerRow: unknown[] = [...columnHeaders, ...stepColumnNames];
  const bodyRows = asins.map((row) =>
    buildRow(row, columnIds, columnIndex, stepColumnNames, valuesByKey)
  );

  return [headerRow, ...bodyRows];
}
